// homeSummary.js — the pure summary math behind the home page's
// board-status widgets (Team pulse, Priority mix, Workload).
//
// S8 of the 2026-08-16 Nika batch: "I like how our board-status py script
// looks — we need a few new builtin widgets for workspace default that show
// similar summaries." The properties worth copying from that script are
// not its ASCII: a proportional bar that never rounds a non-empty bucket
// away, fixed-width count cells so a number growing a digit cannot reflow
// its row, and a legend that names who can act.
//
// Everything here is pure: it takes an array of ALREADY-OPEN tickets and
// returns numbers. The page owns the data plumbing (which tickets are
// loaded, which count as open); this module owns the arithmetic, so the
// "never round to zero" property has exactly one implementation and one
// test file instead of one copy per widget.

/**
 * Largest-remainder allocation of `totalUnits` proportional segments over
 * `buckets`, the same algorithm board-status.py's own `bar()` uses.
 *
 * Two properties the tests pin, both load-bearing:
 *  - a bucket with ANY items gets at least 1 unit (a 1-in-40 bucket must
 *    not disappear — "nothing blocked" when one ticket genuinely is
 *    blocked is a wrong answer, not a rounding detail);
 *  - the emitted units sum to EXACTLY `totalUnits` (the "91 cells against
 *    a 90-column rule" bug the python script's own comment warns about).
 *
 * @param {Array<{key:string,label?:string,hue?:string}>} buckets
 * @param {Record<string, number>} counts  bucket key → item count
 * @param {number} totalUnits
 * @returns {Array<object>} the buckets that earned ≥1 unit, each with `units`
 */
export const barSegments = (buckets, counts, totalUnits) => {
  const total = buckets.reduce((sum, b) => sum + (counts[b.key] || 0), 0)
  if (!total || totalUnits < 1) return []
  const live = buckets.map((b) => ({ ...b, n: counts[b.key] || 0 })).filter((r) => r.n > 0)

  // More non-empty buckets than the bar has units: the min-1 promise
  // cannot hold for all of them, so keep the biggest `totalUnits` and say
  // so by dropping the rest — never by emitting a bar wider than its box.
  if (live.length >= totalUnits) {
    return [...live]
      .sort((a, b) => b.n - a.n)
      .slice(0, totalUnits)
      .map((r) => ({ ...r, units: 1 }))
  }

  // Every non-empty bucket takes 1 unit up front (that IS the min-1
  // property); the REST is what gets shared proportionally. Allocating
  // the full width proportionally and then clamping each floor up to 1
  // is what over-fills the bar — 100/1/1 over 8 units wants 7+1+1 = 9.
  const rest = totalUnits - live.length
  const take = live.map((r) => {
    const share = (r.n / total) * rest
    return { ...r, share, units: 1 + Math.floor(share) }
  })
  let left = totalUnits - take.reduce((sum, r) => sum + r.units, 0)
  const byRemainder = [...take].sort(
    (a, b2) => b2.share - Math.floor(b2.share) - (a.share - Math.floor(a.share))
  )
  for (const r of byRemainder) {
    if (left <= 0) break
    r.units += 1
    left -= 1
  }
  return take
}

// ── Priority mix ────────────────────────────────────────────────────────
// Reuses the tickets package's OWN priority vocabulary and colour mapping
// (PRIORITY_COLOR: urgent→red, high/P1→orange, medium/P2→blue, low/P3→
// caption) rather than inventing a parallel scale, so the home summary and
// the board read the same.
// `label` carries a BUNDLE KEY; the legend's render site in pages/main.js
// resolves it through polyglot (which returns a non-key — a person's name in
// the workload rows below — verbatim).
export const PRIORITY_BUCKETS = [
  { key: 'urgent', label: 'workspace.priority.urgent', hue: 'red' },
  { key: 'high', label: 'workspace.priority.high', hue: 'orange' },
  { key: 'medium', label: 'workspace.priority.medium', hue: 'blue' },
  { key: 'low', label: 'workspace.priority.low', hue: 'caption' }
]

/**
 * A ticket's priority bucket, or `null` when no priority is set.
 * An unset priority is NEVER folded into "Low" — that would report a
 * triage decision nobody made. The widget names those rows in its legend
 * instead ("N to triage"), which is the who-can-act line.
 */
export const priorityBucket = (t) => {
  const key = String(t?.priority || t?.severity || '').toLowerCase()
  if (key === 'urgent' || key === 'p0' || key === 'critical') return 'urgent'
  if (key === 'high' || key === 'p1') return 'high'
  if (key === 'medium' || key === 'normal' || key === 'p2') return 'medium'
  if (key === 'low' || key === 'p3' || key === 'minor') return 'low'
  return null
}

/** @param {Array<object>} openTickets @returns counts incl. `unset` */
export const priorityMix = (openTickets) => {
  const counts = { urgent: 0, high: 0, medium: 0, low: 0, unset: 0 }
  for (const t of openTickets || []) counts[priorityBucket(t) || 'unset'] += 1
  return counts
}

// ── Workload ────────────────────────────────────────────────────────────
// Buckets are dynamic (one per person), so the hue cycles a fixed list of
// design-system tokens — never a raw colour.
export const WORKLOAD_HUES = ['blue', 'orange', 'purple', 'red', 'green']
export const WORKLOAD_MAX_ROWS = 5

/** `name@host` → `name`. Display only; the address itself is never rendered. */
export const workloadName = (email) => String(email || '').split('@')[0] || 'unknown'

/**
 * Open tickets per assignee, biggest load first.
 * Everything past the top `WORKLOAD_MAX_ROWS` folds into one honest
 * "N more" row rather than silently vanishing from a bar that claims to
 * total the board. Unassigned rows are counted separately, never as a
 * person, because "nobody" is the one bucket no one is acting on.
 *
 * @param {Array<object>} openTickets
 * @param {(t:object)=>string} emailOf  reads the assignee address off a row
 */
export const workloadRows = (openTickets, emailOf) => {
  const readEmail = emailOf || ((t) => String(t?.assigneeEmail || '').toLowerCase())
  const byEmail = new Map()
  let unassigned = 0
  for (const t of openTickets || []) {
    const email = readEmail(t)
    if (!email) {
      unassigned += 1
      continue
    }
    byEmail.set(email, (byEmail.get(email) || 0) + 1)
  }
  const sorted = [...byEmail.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  const head = sorted.slice(0, WORKLOAD_MAX_ROWS)
  const tail = sorted.slice(WORKLOAD_MAX_ROWS)
  const rows = head.map(([email, n], i) => ({
    key: email,
    label: workloadName(email),
    hue: WORKLOAD_HUES[i % WORKLOAD_HUES.length],
    n
  }))
  if (tail.length) {
    rows.push({
      key: '_others',
      label: 'workspace.workload.more',
      moreCount: tail.length,
      hue: 'caption',
      n: tail.reduce((sum, [, n]) => sum + n, 0)
    })
  }
  return { rows, unassigned }
}
